<template>
	<div :class="['asset-detail-hero-grid', { 'company-asset': props.isCompanyAsset }]">
		<div class="asset-detail-hero-grid__child pr-300-md">
			<div
				:class="[
					'mb-200-md display-flex',
					{ 'flex-direction-row-reverse-smo justify-content-space-between-smo': showAssetLogo }
				]"
			>
				<div v-if="showAssetLogo" class="asset-logo-container box-shadow mb-100 mr-200" data-test="asset-logo">
					<img :src="assetLogo" alt="" role="presentation" class="asset-logo-img" />
				</div>
				<div>
					<Tag
						v-if="assetStatus"
						:text="assetStatus"
						:background="statusBackground"
						data-test="asset-status-tag"
					/>
					<h1 class="mt-25 heading-1" data-test="asset-name">{{ assetName }}</h1>
					<div
						v-if="tagline && !appStore.isMobile"
						class="body-xl-lg my-50"
						data-test="company-tagline-desktop"
						v-html="tagline"
					/>
					<div
						class="mt-50 display-flex align-items-center-md flex-direction-column-smo text-color-content-secondary"
					>
						<div v-if="assetLocation">{{ assetLocation }}</div>
						<div v-if="yearFounded" class="display-flex align-items-center-md mt-25-smo"
							><BaseSvgIcon
								name="dot"
								height="8"
								width="8"
								:class="[
									'mx-50 text-color-bg-tertiary display-none-smo',
									{ 'display-none': !assetLocation }
								]"
							/>
							Founded
							{{ yearFounded }}
						</div>
					</div>
					<div
						v-if="displayTabletProfilesSection || (appStore.isTablet && website)"
						class="display-flex flex-wrap-wrap align-items-center gap-50 mt-50"
					>
						<AssetDetailsSocialProfiles
							v-if="displayTabletProfilesSection"
							:social-profiles="socialProfiles"
							data-test="tablet-profiles-section"
						/>
						<BaseLink
							v-if="appStore.isTablet && website"
							classes="website-link py-25 px-50 dark ellipsis display-flex align-items-center"
							:link="websiteLink"
							:title="websiteLink.href"
							><BaseSvgIcon name="external-link" width="16" height="16" class="mr-50" /> Visit website
						</BaseLink>
					</div>
				</div>
			</div>
			<div v-if="tagline && appStore.isMobile" data-test="company-tagline-mobile" v-html="tagline" />
			<div
				v-if="displayMobileProfilesSection || (appStore.isMobile && website)"
				class="display-flex flex-wrap-wrap align-items-center gap-50 mt-50"
			>
				<AssetDetailsSocialProfiles
					v-if="displayMobileProfilesSection"
					:social-profiles="socialProfiles"
					data-test="mobile-profiles-section"
				/>
				<BaseLink
					v-if="appStore.isMobile && website"
					classes="website-link py-25 px-50 dark ellipsis display-flex align-items-center"
					:link="websiteLink"
					:title="websiteLink.href"
					><BaseSvgIcon name="external-link" width="16" height="16" class="mr-50" /> Visit website
				</BaseLink>
			</div>
		</div>
		<div class="asset-detail-hero-grid__child mt-200-smo">
			<div class="module overview-table">
				<div
					v-for="detail in overviewDetails"
					:key="detail.key"
					class="display-flex justify-content-space-between p-100 gap-50"
				>
					<div class="text-color-content-secondary" :data-test="`overview-detail-${detail.key}`">
						{{ detail.key }}</div
					>
					<div class="text-overflow-ellipsis" v-html="detail.value"></div>
				</div>
			</div>
		</div>
		<div class="asset-detail-hero-grid__child pr-300-md">
			<div class="mt-200-smo display-flex">
				<div class="display-flex flex-direction-column gap-25 asset-details-hero__content-items">
					<div class="text-color-content-secondary body-sm">Funds</div>
					<AssetDetailsFundTags v-if="props.isCompanyAsset" :funds="funds" />
					<div
						v-else
						:class="[
							'display-flex flex-wrap-wrap gap-50',
							{ 'asset-fund-container': displayDesktopProfilesSection }
						]"
					>
						<div v-for="data in funds" :key="data.fund.fundriseId" class="no-wrap">
							<Tag
								:text="data.fund.name"
								:link="buildOfferingDetailsLink(data.fund.fundriseId, { target: '_blank' })"
							/>
						</div>
					</div>
				</div>
				<div v-if="displayDesktopProfilesSection" class="asset-details-hero__content-items">
					<div class="display-flex flex-direction-column gap-25" data-test="desktop-profiles-section">
						<div class="text-color-content-secondary body-sm" data-test="desktop-profiles-section-header">{{
							profilesSectionHeader
						}}</div>
						<div class="display-flex flex-wrap-wrap align-items-center gap-50">
							<AssetDetailsSocialProfiles
								v-if="displaySocialProfiles"
								:social-profiles="socialProfiles"
							/>
							<BaseLink
								v-if="website"
								classes="website-link py-25 px-50 dark ellipsis display-flex align-items-center"
								:link="websiteLink"
								:title="websiteLink.href"
								><BaseSvgIcon name="external-link" width="16" height="16" class="mr-50" /> Visit website
							</BaseLink>
						</div>
					</div>
				</div>
			</div>
			<RichTextComponent v-if="assetSummary" class="mt-200 mb-200-md" :blocks="assetSummary" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { Asset, AssetFundAllocation } from 'types/assets';
import { KeyValue, Link, TagBackground } from 'types/layout';
import { SanityProxyLink, SanityRichTextBlock } from 'types/sanity';
import AssetDetailsFundTags from '@components/marketing-site/asset-details/asset-details-fund-tags.vue';
import AssetDetailsSocialProfiles from '@components/marketing-site/asset-details/asset-details-social-profiles.vue';
import { buildOfferingDetailsLink } from '@utils/offerings';
import { computed } from 'vue';
import { deorphanText } from '@filters/shared-filters';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import Tag from '@components/tags/tag-component.vue';
import { useAppStore } from '@stores/app';

interface Props {
	asset: Asset;
	isCompanyAsset?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isCompanyAsset: false
});

const appStore = useAppStore();

const showAssetLogo = computed((): boolean => !!props.asset.thumbnail);

const assetLogo = computed((): string => props.asset.thumbnail?.url ?? '');

const assetStatus = computed((): string => props.asset.status.label ?? '');

const displayDesktopProfilesSection = computed(
	(): boolean =>
		(!!props.asset.ventureCapitalFields?.socialMediaLinks || !!props.asset.ventureCapitalFields?.website) &&
		appStore.isDesktop
);

const displayTabletProfilesSection = computed(
	(): boolean => !!props.asset.ventureCapitalFields?.socialMediaLinks && appStore.isTablet
);

const displayMobileProfilesSection = computed(
	(): boolean => !!props.asset.ventureCapitalFields?.socialMediaLinks && appStore.isMobile
);

const isAssetActive = computed((): boolean => props.asset.status?.name === 'ACTIVE');

const statusBackground = computed((): TagBackground => {
	if (isAssetActive.value) {
		return 'green';
	} else {
		return 'default';
	}
});

const assetName = computed((): string => props.asset.name ?? props.asset.assetType.name);

const assetLocation = computed((): string => {
	const city = props.asset.ventureCapitalFields?.city;
	const state = props.asset.ventureCapitalFields?.state;

	if (city && state) {
		return `${city}, ${state}`;
	} else {
		return '';
	}
});

const yearFounded = computed((): string => props.asset.ventureCapitalFields?.yearFounded ?? '');

const tagline = computed((): string =>
	props.asset.ventureCapitalFields?.tagline ? deorphanText(props.asset.ventureCapitalFields.tagline) : ''
);

const funds = computed((): Array<AssetFundAllocation> => props.asset.fundAllocations);

const website = computed((): string => props.asset.ventureCapitalFields?.website ?? '');

const websiteLink = computed((): Link => {
	return { href: website.value, target: '_blank' };
});

const socialProfiles = computed((): Array<SanityProxyLink> => props.asset.ventureCapitalFields?.socialMediaLinks ?? []);

const displaySocialProfiles = computed((): boolean => socialProfiles.value.length > 0);

const assetSummary = computed((): Array<SanityRichTextBlock> | undefined => props.asset.summary);

const profilesSectionHeader = computed((): string => (website.value ? 'Profiles' : 'Website'));

const overviewDetails = computed((): Array<KeyValue> => {
	return [
		{ key: 'Asset type', value: props.asset.assetType.name },
		{ key: 'Industry', value: props.asset.ventureCapitalFields?.industry?.abbreviation ?? '' },
		{ key: 'Company size', value: props.asset.ventureCapitalFields?.headcount ?? '' }
	].filter((item) => !!item.value);
});
</script>

<style lang="scss" scoped>
@use '../../../styles/constants/_colors.scss' as *;
@use '../../../styles/utilities/respond-to.scss' as *;

.asset-details-hero {
	&__content-items {
		&:not(:first-of-type) {
			padding-left: 2rem;
		}
		&:not(:last-of-type) {
			padding-right: 2rem;
			border-right: 1px solid hsl(210, 10%, 90%);
		}
	}
}

.asset-logo {
	&-container {
		width: 100px;
		height: 100px;
		border-radius: 8px;
		box-shadow: 0px 2px 10px 0px rgba(41, 43, 62, 0.08);
		flex-shrink: 0;

		@include respond-to(md) {
			width: 116px;
			height: 116px;
		}
	}
	&-img {
		border-radius: 8px;
	}
}

.module.overview-table {
	border: 1px solid token('bg-tertiary');
	border-radius: 8px;
	box-shadow: none;
	@include respond-to(md) {
		margin-top: 0;
	}

	& > *:not(:last-of-type) {
		border-bottom: 1px solid token('bg-tertiary');
	}
}

.website-link {
	border-radius: 8px;
	border: 1px solid token('bg-tertiary');
	max-width: 270px;

	&:hover {
		color: token('content-primary');
	}
}

.asset-fund-container {
	width: min-content;
}

.asset-detail-hero-grid {
	display: grid;

	@include respond-to(smo) {
		grid-template-rows: 1fr;

		.asset-detail-hero-grid__child {
			&:nth-child(2) {
				grid-area: 3;
			}
		}
	}

	@include respond-to(md) {
		grid-template-columns: 2fr 1fr;
		&.company-asset {
			.asset-detail-hero-grid__child {
				&:nth-child(2) {
					grid-area: 2/2/3/3;
				}

				&:nth-child(3) {
					grid-area: 2/1/3/2;
				}
			}
		}
	}
}
</style>
